import {
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Loading } from 'react-admin';
import { applyEffect } from './resolveEffectiveRate';
import {
  countScopeByMarket,
  regionTarget,
  scopeMatchesRegion,
} from './rateRuleScope';
import { useRateRuleCatalog } from './useRateRuleCatalog';
import { candidateToRule, type CandidateRule } from './candidateRule';
import { formatRate } from '../stores/formatRate';

/**
 * SCOPE PREVIEW (D-rate-rules). A PROJECTION over today's catalog: how many
 * stores a rule's scope affects, per market, with a few sample base -> effective
 * rates. A real filter over the catalog the multi-channel lead established, never
 * a faked number. It is NOT the live per-click resolution.
 */
export const RuleScopePreview = ({
  candidate,
}: {
  candidate: CandidateRule;
}) => {
  const { views, isLoading } = useRateRuleCatalog();
  if (isLoading) return <Loading />;

  const rule = candidateToRule(candidate);
  const count = countScopeByMarket(rule, views);
  const inScope = views
    .filter(
      (v) =>
        (rule.market === v.market || rule.market === 'all') &&
        scopeMatchesRegion(rule, v),
    )
    .slice(0, 5);

  return (
    <Box data-testid="scope-preview">
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Scope preview
      </Typography>
      <Alert severity="info" sx={{ mb: 1, py: 0 }}>
        A projection over today&apos;s catalog and rules, not the live per-click
        resolution (which the backend runs server-side).
      </Alert>
      <Typography variant="body2" data-testid="scope-count" sx={{ mb: 1 }}>
        Affects {count.total} store{count.total === 1 ? '' : 's'}
        {Object.keys(count.byMarket).length > 0
          ? ' (' +
            (['uk', 'us', 'ie'] as const)
              .filter((m) => count.byMarket[m])
              .map((m) => `${count.byMarket[m]} ${m.toUpperCase()}`)
              .join(', ') +
            ')'
          : ''}
        .
      </Typography>
      {inScope.length > 0 && (
        <Table size="small" aria-label="sample effective rates">
          <TableHead>
            <TableRow>
              <TableCell>Store</TableCell>
              <TableCell>Market</TableCell>
              <TableCell>Base</TableCell>
              <TableCell>Effective</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inScope.map((v) => {
              const base = regionTarget(v).baseRate;
              return (
                <TableRow key={v.regionId}>
                  <TableCell>#{v.regionId}</TableCell>
                  <TableCell>{v.market.toUpperCase()}</TableCell>
                  <TableCell>{formatRate(base)}</TableCell>
                  <TableCell>{formatRate(applyEffect(base, rule))}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};
